import { useEffect, useMemo, useState } from "react";
import { api, type CalendarData, type Horizon, type Session } from "../api.ts";
import { addDays, captionStyle, colors, serif, shortDate, todayISO, tokens } from "../theme.ts";
import { DayList, buildDays } from "../components/DayList.tsx";
import { GlassButton, GlassCard, inputStyle } from "../components/glass.tsx";
import { TaskRow } from "../components/TaskRow.tsx";
import { FlagCard } from "./FlagCard.tsx";

/** The weekly question. The API holds no prompt line for it, so it is fixed here. It sets where the week went beside what is said to matter, and asks for nothing to be rated or compared with last week. */
const PROMPT = "Where did this week actually go, and how does that sit beside what you say matters?";

/**
 * The weekly session, available any day, in three panels: the 21-day horizon
 * with what has to start now, one open flag, and one reflection field. The
 * reflection is saved the way Home's evening note is: a log note, then the
 * sitting recorded (as "weekly"). Nothing is rated and nothing is compared.
 */
export function Week({ version, onOpen, onChanged }: { version: number; onOpen: (id: string) => void; onChanged: () => void }) {
  const [horizon, setHorizon] = useState<Horizon | null>(null);
  const [cal, setCal] = useState<CalendarData | null>(null);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [error, setError] = useState<string | null>(null);
  const today = todayISO();

  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const load = (live: () => boolean = () => true) => {
    Promise.all([api.horizon(21), api.calendar(today, addDays(today, 21)), api.sessions(7).catch(() => [] as Session[])])
      .then(([h, c, s]) => { if (live()) { setHorizon(h); setCal(c); setSessions(s); setError(null); } })
      .catch((e) => live() && setError(e.message));
  };
  useEffect(() => {
    let alive = true;
    load(() => alive);
    return () => { alive = false; };
  }, [version, today]);

  const days = useMemo(
    () => (horizon && cal ? buildDays(horizon.dated, cal.events, cal.sources, today) : []),
    [horizon, cal, today],
  );
  const recorded = saved || sessions.some((s) => s.kind === "weekly" && s.date === today);
  const flag = horizon?.flags[0] ?? null;

  const submit = async () => {
    const said = text.trim();
    if (!said) return;
    setBusy(true); setSaveError(null);
    try {
      await api.log({ kind: "note", text: said });
      try { await api.recordSession({ kind: "weekly", transcript: said, wrote: [{ logged: "note" }] }); }
      catch { /* the note is saved; only the record of the sitting failed */ }
      setSaved(true);
      onChanged();
    } catch (e: any) { setSaveError(e?.message ?? "that did not go through"); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ padding: `12px ${tokens.space.gutter}px 0` }}>
      <div style={{
        marginBottom: 14, fontFamily: serif, fontSize: tokens.type.screenTitle.size,
        lineHeight: tokens.type.screenTitle.lineHeight, color: colors.ink,
      }}>Week</div>

      {error && <p style={{ margin: "0 0 12px", fontSize: 14, color: colors.ink }}>{error}</p>}
      {!horizon && !error && <p style={{ margin: 0, fontSize: 15, color: colors.ink }}>Opening…</p>}

      {horizon && cal && (
        <>
          <Panel title="The next 21 days" defaultOpen>
            {days.length === 0 && (
              <GlassCard padding={16} style={{ marginTop: 14 }}>
                <p style={{ margin: 0, fontSize: 15, color: colors.ink2 }}>Nothing dated.</p>
              </GlassCard>
            )}
            <DayList days={days} today={today} onOpenTask={onOpen} />
          </Panel>

          {horizon.lead_time.length > 0 && (
            <Panel title="Start now" defaultOpen>
              <GlassCard padding="2px 16px" style={{ marginTop: 14 }}>
                {horizon.lead_time.map((t) => (
                  <TaskRow
                    key={t.id} task={t} onOpen={() => onOpen(t.id)}
                    meta={`for ${t.starts_now_for.map((s) => (s.due ? `${s.title}, ${shortDate(s.due)}` : s.title)).join("; ")}`}
                  />
                ))}
              </GlassCard>
            </Panel>
          )}

          {flag && <FlagCard flag={flag} today={today} onResolved={() => { load(); onChanged(); }} />}

          <GlassCard padding={16} style={{ marginTop: 16 }}>
            <div style={{
              fontFamily: serif, fontStyle: "italic", fontSize: tokens.type.question.size,
              lineHeight: tokens.type.question.lineHeight, color: colors.ink, marginBottom: 12,
            }}>{PROMPT}</div>
            {recorded ? (
              <p style={{ margin: 0, fontSize: 14, color: colors.ink }}>Recorded today.</p>
            ) : (
              <div>
                <textarea
                  value={text} onChange={(e) => setText(e.target.value)} rows={4} placeholder="Write what you like"
                  style={{ ...inputStyle, resize: "none" }}
                />
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
                  <GlassButton variant="primary" disabled={busy || !text.trim()} onClick={submit}>{busy ? "…" : "Save"}</GlassButton>
                </div>
                {saveError && <p style={{ margin: "8px 0 0", fontSize: 12.5, color: colors.ink }}>{saveError}</p>}
              </div>
            )}
          </GlassCard>
        </>
      )}
    </div>
  );
}

/** A collapsible group: a header card, and what it holds under it. */
function Panel({ title, defaultOpen, children }: { title: string; defaultOpen?: boolean; children: React.ReactNode }) {
  const [open, setOpen] = useState(!!defaultOpen);
  return (
    <div style={{ marginBottom: 16 }}>
      <GlassCard padding={0}>
        <button
          onClick={() => setOpen(!open)} aria-expanded={open}
          style={{
            display: "flex", width: "100%", justifyContent: "space-between", alignItems: "center",
            padding: "14px 16px", background: "transparent", border: "none", cursor: "pointer", textAlign: "left",
            ...captionStyle,
          }}
        >
          <span>{title}</span>
          <span aria-hidden style={{ fontSize: 14, transform: open ? "rotate(90deg)" : undefined }}>›</span>
        </button>
      </GlassCard>
      {open && children}
    </div>
  );
}
